import React, { useState, useEffect } from "react";
import { Box, Card, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import TaskSidebar from "./components/TaskSidebar";
import TaskList from "./components/TaskList";
import TaskDetails from "./components/TaskDetails";
import InformationStepsCard from "../../components/InformationStepsCard";

const TaskHome = () => {
  const theme = useTheme();
  const location = useLocation();
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState("__all__");
  const [preselectedTaskId, setPreselectedTaskId] = useState(null);
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get("taskId");
    if (taskId) {
      setPreselectedTaskId(taskId);
      setSelectedTask(null);
    } else {
      setPreselectedTaskId(null);
    }
  }, [location.search]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        backgroundColor: theme.customColors.background.header,
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page shows all tasks required to achieve compliance with your selected frameworks (e.g., **SOC 2**, **ISO 27001**)."
          steps={[
            "Tasks are automatically generated based on your compliance framework requirements and current security posture.",

            "**Open Tasks** require your attention - these could be integration setups, policy approvals, or security check failures that need resolution.",

            "**Resolved Tasks** have been completed. You can review them to track your compliance progress.",

            "Use domain filters (**Infrastructure**, **Risks**, **People**) to focus on specific areas of compliance.",

            "Click on any task to see detailed remediation steps and navigate directly to the relevant section of the platform.",
          ]}
          footerText="💡 Your aim is to complete all open tasks to achieve compliance with your selected frameworks."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Box sx={{ px: 2, mb: 2 }}>
        <Card
          sx={{
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #E8EAED",
            px: 2,
            py: 1.5,
          }}
        >
          <TaskSidebar
            selectedDomain={selectedDomain}
            setSelectedDomain={setSelectedDomain}
            selectedOwner={selectedOwner}
            setSelectedOwner={setSelectedOwner}
          />
        </Card>
      </Box>

      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          px: 2,
          pb: 2,
          gap: 2,
        }}
      >
        <TaskList
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
          selectedDomain={selectedDomain}
          selectedOwner={selectedOwner}
          preselectedTaskId={preselectedTaskId}
          refreshKey={tasksRefreshKey}
          onTaskOwnerChanged={() => setTasksRefreshKey((prev) => prev + 1)}
        />

        <TaskDetails
          task={selectedTask}
          onTaskUpdated={setSelectedTask}
          onTaskOwnerChanged={() => setTasksRefreshKey((prev) => prev + 1)}
        />
      </Box>
    </Box>
  );
};

export default TaskHome;
